import React from "react";
import {
  AppBar as MuiAppBar,
  Chip,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { ArrowCircleLeftSharp, Menu as MenuIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import "../../css/appbar.css";
import logoExpo from "../../images/logo.png";
import { useRouterStore } from "../../../libs/stores";
import { useWidth } from "../../../libs/hooks";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "../../../libs/shared/components";

export const drawerWidth = 180;

const propsTooltip = {
  tooltip: {
    sx: {
      background: "#e8670a4b",
      color: "#ffffff",
      fontSize: "11px",
      fontWeight: 600,
      borderRadius: "6px",
      px: 1.2,
      py: 0.5,
    },
  },
  arrow: {
    sx: { color: "#e8670a4b" },
  },
};

// ── Styled AppBar ─────────────────────────────────────────────────────────────
const AppBarElement = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "small",
})<{ open?: boolean; small?: string }>(({ theme, open, small }) => ({
  height: "57px",
  minHeight: "57px",
  maxHeight: "57px",
  zIndex: 1001,
  background: "linear-gradient(135deg, #17143a 0%, #08000a 100%)",
  boxShadow: "0 2px 16px rgba(83, 74, 183, 0.35)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: small === "true" ? 0 : drawerWidth,
    width: small === "true" ? "100%" : `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ── AppBar ────────────────────────────────────────────────────────────────────
export const AppBar = ({
  authTokens,
  handleDrawerOpen,
  open,
}: {
  authTokens?: any;
  handleDrawerOpen: () => void;
  open: boolean;
}) => {
  const width = useWidth();
  const navigate = useNavigate();
  const isSmall = width === "xs";

  return (
    <React.Fragment>
      <AppBarElement
        position="fixed"
        open={open}
        small={isSmall ? "true" : "false"}
      >
        <Toolbar className="toolbar">
          {/* ── Izquierda: menú + volver ── */}
          <div className="appbar-left">
            {/* Botón hamburguesa — oculto cuando drawer está abierto en desktop */}
            {(!open || isSmall) && (
              <Tooltip
                title="Abrir menú"
                placement="bottom"
                arrow
                componentsProps={propsTooltip}
              >
                <IconButton
                  className="appbar-menu-btn"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                >
                  <MenuIcon style={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Botón volver con Tooltip */}
            <Tooltip
              title="Página anterior"
              placement="right"
              arrow
              componentsProps={propsTooltip}
            >
              <Button
                className="appbar-back-btn"
                onClick={() => navigate(-1)}
                size="small"
              >
                <ArrowCircleLeftSharp style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          </div>

          {/* ── Centro: logo + separador + chip sección ── */}
          <div className="appbar-center">
            <img src={logoExpo} alt="ON-AIR" className="logo" />
            <ElChip />
          </div>

          {/* ── Derecha: avatar ── */}
          <div className="appbar-right">
            <div className="avatar-contain">
              <UserAvatar />
            </div>
          </div>
        </Toolbar>
      </AppBarElement>
    </React.Fragment>
  );
};

// ── Chip de sección activa ────────────────────────────────────────────────────
export const ElChip = () => {
  const section = useRouterStore((state) => state.section);
  if (!section) return null;

  return (
    <>
      <div className="appbar-separator" />
      <Chip
        className="appbar-chip"
        title={section}
        color="secondary"
        size="medium"
        label={section}
      />
    </>
  );
};
